import ShimmerPlaceHolder, {
  createShimmerPlaceHolder,
} from 'expo-shimmer-placeholder'
import { LinearGradient } from 'expo-linear-gradient'
import { createRef, useEffect } from 'react'
import { Animated, View } from 'react-native'
import { Card } from 'react-native-paper'
import Column from '../grid/column'
import Row from '../grid/row'

const ShimmerPlaceholder = createShimmerPlaceHolder(LinearGradient)

/** Near-flat grays so motion stays visible without a harsh horizontal band. */
const ADMIN_STAT_SHIMMER_COLORS = ['#e9e9e9', '#e3e3e3', '#e9e9e9'] as const
const ADMIN_STAT_SHIMMER_LOCATIONS: [number, number, number] = [0.46, 0.5, 0.54]

const shimmerCommon = {
  shimmerColors: [...ADMIN_STAT_SHIMMER_COLORS],
  location: ADMIN_STAT_SHIMMER_LOCATIONS,
  shimmerWidthPercent: 0.92 as const,
}

/**
 * Full admin stat metric card placeholder (header + two-column figures).
 * Matches `AdminStatMetricCard` outer layout while data loads.
 */
export function AdminStatMetricCardSkeleton() {
  const iconRef = createRef<ShimmerPlaceHolder>()
  const titleRef = createRef<ShimmerPlaceHolder>()
  const curMainRef = createRef<ShimmerPlaceHolder>()
  const curSubRef = createRef<ShimmerPlaceHolder>()
  const prevMainRef = createRef<ShimmerPlaceHolder>()
  const prevSubRef = createRef<ShimmerPlaceHolder>()

  useEffect(() => {
    const icon = iconRef.current
    const title = titleRef.current
    const curMain = curMainRef.current
    const curSub = curSubRef.current
    const prevMain = prevMainRef.current
    const prevSub = prevSubRef.current
    if (!icon || !title || !curMain || !curSub || !prevMain || !prevSub) return
    const animation = Animated.stagger(65, [
      icon.getAnimated(),
      title.getAnimated(),
      curMain.getAnimated(),
      curSub.getAnimated(),
      prevMain.getAnimated(),
      prevSub.getAnimated(),
    ])
    Animated.loop(Animated.sequence([animation, Animated.delay(400)])).start()
  }, [])

  return (
    <Card style={{ width: '100%' }}>
      <Card.Content style={{ gap: 8 }}>
        <Row alignItems="center" columnGap={2}>
          <ShimmerPlaceholder
            ref={iconRef}
            width={28}
            height={28}
            stopAutoRun
            {...shimmerCommon}
            shimmerStyle={{ borderRadius: 14 }}
          />
          <View style={{ flex: 1, minWidth: 0 }}>
            <ShimmerPlaceholder
              ref={titleRef}
              height={18}
              width="75%"
              stopAutoRun
              {...shimmerCommon}
              shimmerStyle={{ borderRadius: 4 }}
            />
          </View>
        </Row>

        <Row columnGap={12} alignItems="flex-start">
          <Column gap={8} style={{ flex: 1, minWidth: 0 }}>
            <ShimmerPlaceholder
              ref={curMainRef}
              height={44}
              width="88%"
              stopAutoRun
              {...shimmerCommon}
              shimmerStyle={{ borderRadius: 6 }}
            />
            <ShimmerPlaceholder
              ref={curSubRef}
              height={14}
              width="55%"
              stopAutoRun
              {...shimmerCommon}
              shimmerStyle={{ borderRadius: 4 }}
            />
          </Column>
          <Column
            gap={8}
            style={{ width: '42%', maxWidth: 140, flexShrink: 0 }}
          >
            <ShimmerPlaceholder
              ref={prevMainRef}
              height={26}
              width="100%"
              stopAutoRun
              {...shimmerCommon}
              shimmerStyle={{ borderRadius: 4 }}
            />
            <ShimmerPlaceholder
              ref={prevSubRef}
              height={14}
              width="72%"
              stopAutoRun
              {...shimmerCommon}
              shimmerStyle={{ borderRadius: 4 }}
            />
          </Column>
        </Row>
      </Card.Content>
    </Card>
  )
}
